import merge from 'lodash/merge'
import React, { useContext as useContextCore } from 'react'
import type { Components } from './components'
import { components as defaultComponents } from './components'
import type { ListField, ListFieldConfig, UseListResult } from '@ztechapps/common.hook'
import { useDataToolFields } from '../hooks/useDataToolFields'
import { ShowAllFilterTemplate } from './constants'

export type DataToolProps = {
  searchPlaceHolder?: string
  filterCategory?: string
  fields?: ListField[]
  fieldsConfig?: ListFieldConfig
  children?: React.ReactNode
  components?: Components
  onRefresh?: () => Promise<any>
  onAdd?: (options: any) => Promise<void>
  searchByField?: boolean
  containerStyle?: any
  debounceTime?: number
  searchOnChange?: boolean
} & Partial<UseListResult>

type ContextType = Omit<DataToolProps, 'children' | 'fields' | 'refetch'> & {
  commonFields: {
    sortFields: ListField[]
    filterFields: ListField[]
    searchFields: ListField[]
  }
  onReset: () => Promise<void>
}

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const Provider = ({
  children,
  components,
  onRefresh,
  onAdd,
  onFilter,
  searchable,
  onSort,
  filter,
  sort,
  searchValue,
  filterTemplate,
  searchPlaceHolder,
  refetch,
  filterCategory,
  fields,
  searchByField = true,
  containerStyle = {},
  onChangeSearchField,
  onChangeSearchType,

  ...props
}: DataToolProps) => {
  //merge with the default components
  components = merge({}, defaultComponents, components)

  fields = !fields ? Object.values(props.fieldsConfig) : fields

  //default search
  searchPlaceHolder = searchPlaceHolder ?? 'Search the list.'

  //if check also refetch (which is apollo default prop for fetching again) when onRefresh is no available
  onRefresh = onRefresh || (() => refetch?.())

  //setup the fields
  const commonFields = useDataToolFields(fields)

  const onReset = async () => {
    onFilter({
      tableFilter: {
        __reset: { value: true },
      },
      searchValue: '',
      searchObject: {},
      template: ShowAllFilterTemplate,
    })
  }

  return (
    <CoreProvider
      value={{
        ...props,
        onRefresh,
        onAdd,
        onFilter,
        searchable,
        onSort,
        searchPlaceHolder,
        components,
        filterCategory,
        filterTemplate,
        containerStyle,
        commonFields,
        filter,
        sort,
        searchValue,
        searchByField,
        onChangeSearchField,
        onChangeSearchType,
        onReset,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useContext = () => useContextCore(context)
